import { DayAvailability } from '@/components/Schedule/DayAvailability'
import { Schedule } from '@/components/Schedule/Schedule'
import { CalendarType } from '@/models/calendar'
import AppState from '@/states/app'
import moment from 'moment'
import { useI18n } from 'vue-i18n'

export function useAvailabilityText() {
  const { t } = useI18n()
  return (scheduleObj: any) => {
    if (!scheduleObj) {
      return t('settings.availability.schedule.no_availability')
    }
    const schedule = new Schedule(scheduleObj)
    // Helper: day name to abbreviation
    const dayAbbr: Record<string, string> = {
      sunday: 'Sun',
      monday: 'Mon',
      tuesday: 'Tue',
      wednesday: 'Wed',
      thursday: 'Thu',
      friday: 'Fri',
      saturday: 'Sat',
    }
    function formatTime(time: string) {
      return moment(time, 'H:m').format('h:mm A')
    }
    function intervalsEqual(a: any[], b: any[]): boolean {
      if (a.length !== b.length) {
        return false
      }
      for (let i = 0; i < a.length; i++) {
        if (a[i].from !== b[i].from || a[i].to !== b[i].to) {
          return false
        }
      }
      return true
    }
    function getTimeRange(intervals: any[]) {
      if (!intervals || intervals.length === 0) {
        return ''
      }
      if (intervals.length === 1) {
        return `${formatTime(intervals[0].from)} to ${formatTime(
          intervals[0].to
        )}`
      }
      return intervals
        .map(i => `${formatTime(i.from)} to ${formatTime(i.to)}`)
        .join(', ')
    }
    function allIntervalsSame(days: any[]) {
      if (days.length === 0) {
        return false
      }
      const first = JSON.stringify(days[0].intervals)
      return days.every(d => JSON.stringify(d.intervals) === first)
    }
    const weekly = schedule.getWeeklySchedule().filter(d => d.isActive)
    const weekendDays = ['saturday', 'sunday']
    const weekdayDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
    const activeWeekdays = weekly.filter(d => weekdayDays.includes(d.day!))
    const activeWeekend = weekly.filter(d => weekendDays.includes(d.day!))
    const getDayIntervals = (days: any[]) => days.map(d => d.intervals)
    // Priority: if any wday rules are active, use wday logic
    if (
      weekly.length > 0 &&
      weekly.some(d => d.isActive && d.intervals && d.intervals.length > 0)
    ) {
      if (
        weekly.length === 0 ||
        weekly.every(d => !d.isActive || !d.intervals.length)
      ) {
        return t('settings.availability.schedule.no_availability')
      }
      // 1 & 2. Monday to Friday
      if (activeWeekdays.length === 5 && activeWeekend.length === 0) {
        if (allIntervalsSame(activeWeekdays)) {
          const intervals = activeWeekdays[0].intervals
          return t('settings.availability.schedule.weekdays', {
            timeRange: getTimeRange(intervals),
          })
        }
        return t('settings.availability.schedule.weekdays_hours_vary')
      }
      // 5 & 6. Saturday to Sunday
      if (activeWeekend.length === 2 && activeWeekdays.length === 0) {
        if (allIntervalsSame(activeWeekend)) {
          const intervals = activeWeekend[0].intervals
          return t('settings.availability.schedule.weekend', {
            timeRange: getTimeRange(intervals),
          })
        }
        return t('settings.availability.schedule.weekend_hours_vary')
      }
      // 3 & 4. Some weekdays or mixed days
      if (weekly.length > 0) {
        const activeDays = weekly.filter(d => d.isActive)
        if (activeDays.length > 0) {
          const abbrs = activeDays.map(d => dayAbbr[d.day!])
          if (allIntervalsSame(activeDays)) {
            const intervals = activeDays[0].intervals
            return `${abbrs.join(', ')}, ${getTimeRange(intervals)}`
          }
          return t('settings.availability.schedule.days_hours_vary', {
            days: abbrs.join(', '),
          })
        }
      }
      return t('settings.availability.schedule.no_availability')
    }
    // If no wday rules are active, check for date-specific rules
    const dateSpecific = schedule
      .getDateSpecificHours()
      .filter(d => d.isActive && d.date)
    if (dateSpecific.length > 0) {
      // const d = dateSpecific[0]
      // const dateStr = moment(d.date, 'YYYY-MM-DD').format('D MMMM')
      if (dateSpecific.length === 1) {
        return t('settings.availability.schedule.date_specific_hours_1')
      }
      return t('settings.availability.schedule.date_specific_hours', {
        count: dateSpecific.length,
      })
    }
    // Fallback
    return t('settings.availability.schedule.no_availability')
  }
}

/**
 * Converts a calendar to a Schedule instance.
 * Maps openHours to rules (DayAvailability[]).
 * @param calendar The calendar object from API response
 * @returns Schedule
 */
export function createScheduleFromCalendarResponse(calendar: any): Schedule {
  // Map openHours to DayAvailability rules
  const rules: DayAvailability[] = (calendar.openHours || []).map((oh: any) => {
    // Map daysOfTheWeek (array of numbers) to day string
    // 0: Sunday, 1: Monday, ... 6: Saturday
    const dayMap = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ]
    const dayIdx = (oh.daysOfTheWeek && oh.daysOfTheWeek[0]) || 0
    const day = dayMap[dayIdx]
    return new DayAvailability({
      type: 'wday',
      day,
      intervals: (oh.hours || []).map((h: any) => ({
        from: `${h.openHour}:${h.openMinute}`,
        to: `${h.closeHour}:${h.closeMinute}`,
      })),
      isActive: (oh.hours || []).length > 0,
    })
  })

  return new Schedule({
    id: calendar.id,
    name: calendar.name,
    userId:
      calendar.calendarType === CalendarType.EVENT
        ? ''
        : calendar.createdBy?.userId || '',
    calendarIds: [calendar.id],
    rules,
    timezone: AppState.locationTimezone || '',
    locationId: calendar.locationId || '',
    isCustom: true,
  })
}
